import { Component, EventEmitter, Input, Output } from '@angular/core';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faChevronDown, faChevronRight, faCopy, faPen, faSort, faSortDown, faSortUp, faTrash } from '@fortawesome/free-solid-svg-icons';
import { CarpentryOrder } from 'src/app/models/configurators-2d/applications/carpentry/carpentry-order';
import { QuotationRecord } from 'src/app/models/configurators-2d/applications/carpentry/quotation-record';
import { hasCarpentrySupplierSendingError } from 'src/app/services/helpers/carpentry-order-display.helper';
import {
  CarpentryProductListSortDirection,
  CarpentryProductListSortField,
  CarpentryProductListSortDirections,
  getDefaultCarpentryProductListSortDirections,
} from 'src/app/models/configurators-2d/applications/carpentry/carpentry-product-list-sort.model';

@Component({
  selector: 'ng-casto-carpentry-product-list',
  templateUrl: './carpentry-product-list.component.html',
  styleUrls: ['./carpentry-product-list.component.scss'],
})
export class CarpentryProductListComponent {
  readonly CarpentryProductListSortField = CarpentryProductListSortField;

  @Input() products: QuotationRecord[] = [];
  @Input() totalAmount: number = 0;
  @Input() discountOptionsMap: Record<string, { label: string; value: number }[]> = {};
  @Input() tvaOptions: any[] = [];
  @Input() loader = false;
  @Input() expandedRows: any = {};
  @Input() displayPrintBtn = false;
  @Input() displayCloseBtn = false;
  @Input() tableMaxHeight: string | null = null;
  @Input() readOnly = false;
  @Input() latestOrder: CarpentryOrder | undefined;
  @Input() sortDirections: CarpentryProductListSortDirections = getDefaultCarpentryProductListSortDirections();

  @Output() addProduct = new EventEmitter<void>();
  @Output() edit = new EventEmitter<QuotationRecord>();
  @Output() duplicateRecord = new EventEmitter<QuotationRecord>();
  @Output() delete = new EventEmitter<QuotationRecord>();
  @Output() quantityChange = new EventEmitter<QuotationRecord>();
  @Output() discountChange = new EventEmitter<{ product: QuotationRecord; value: number }>();
  @Output() tvaChange = new EventEmitter<QuotationRecord>();

  @Output() orderEdition = new EventEmitter<void>();
  @Output() print = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();
  @Output() sortToggle = new EventEmitter<CarpentryProductListSortField>();

  faPen = faPen;
  faCopy = faCopy;
  faTrash = faTrash;
  faChevronDown = faChevronDown;
  faChevronRight = faChevronRight;
  faSort = faSort;
  faSortUp = faSortUp;
  faSortDown = faSortDown;

  isProjectTotalExpanded = false;

  get allRowsExpanded(): boolean {
    return this.products.length > 0 && Object.keys(this.expandedRows).length === this.products.length;
  }

  toggleAllRows() {
    if (this.allRowsExpanded) {
      this.expandedRows = {};
    } else {
      const newExpandedRows: any = {};
      this.products.forEach(p => {
        newExpandedRows[p.externalId] = true;
      });
      this.expandedRows = newExpandedRows;
    }
  }

  get totalsBySupplier(): { supplierName: string; total: number }[] {
    const map = new Map<string, number>();
    for (const p of this.products) {
      const key = p.supplierName || 'Inconnu';
      map.set(key, (map.get(key) ?? 0) + p.totalPriceDiscountTtc);
    }
    return Array.from(map.entries()).map(([supplierName, total]) => ({ supplierName, total }));
  }

  isPendingOrderNumber(record: QuotationRecord) {
    return !record.supplierSent && !record.orderNumber && this.latestOrder?.opportunityNumber;
  }

  isWaitingPayment(record: QuotationRecord) {
    return !record.supplierSent && record.orderNumber && this.latestOrder?.opportunityNumber;
  }

  hasUnknownOrderNumber(record: QuotationRecord) {
    const orderRefNumbers = this.latestOrder?.orderRefNumbers;
    return !record.supplierSent && record.orderNumber && this.latestOrder?.opportunityNumber
      && orderRefNumbers && orderRefNumbers.length > 0 && !orderRefNumbers.find(o => o.toString() === record.orderNumber);
  }

  hasSupplierSendingError(record: QuotationRecord): boolean {
    return hasCarpentrySupplierSendingError(this.latestOrder, record);
  }

  onQuantityChange(p: QuotationRecord) {
    this.quantityChange.emit(p);
  }

  onDiscountChange(p: QuotationRecord, value: number) {
    this.discountChange.emit({ product: p, value });
  }

  onTvaChange(p: QuotationRecord) {
    this.tvaChange.emit(p);
  }

  editRecord(p: QuotationRecord) {
    this.edit.emit(p);
  }

  copyRecord(p: QuotationRecord) {
    this.duplicateRecord.emit(p);
  }

  deleteRecord(p: QuotationRecord) {
    this.delete.emit(p);
  }

  // Choisit l'icône affichée dans l'en-tête de tri.
  getSortIcon(field: CarpentryProductListSortField): IconDefinition | null {
    const direction = this.sortDirections[field];
    if (direction === CarpentryProductListSortDirection.Asc) {
      return this.faSortUp;
    }

    if (direction === CarpentryProductListSortDirection.Desc) {
      return this.faSortDown;
    }

    return this.faSort;
  }

  // Indique au template si la colonne est triée.
  isSortActive(field: CarpentryProductListSortField): boolean {
    return this.sortDirections[field] !== CarpentryProductListSortDirection.None;
  }

  // Remonte le changement de tri au parent.
  onSortToggle(field: CarpentryProductListSortField) {
    this.sortToggle.emit(field);
  }

  onAddProduct() {
    this.addProduct.emit();
  }

  onPrint() {
    this.print.emit();
  }

  onClose() {
    this.close.emit();
  }
}
